'use client';

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export type PageBannerTone = 'success' | 'info' | 'warning' | 'danger';

export interface PageBannerState {
  /** Counter intern, unique per show() call — folosit ca dependency pentru reset timer auto-dismiss. */
  id: number;
  label: string;
  tone: PageBannerTone;
  /** ms; default 3000 */
  duration: number;
}

export interface PageBannerContextValue {
  banner: PageBannerState | null;
  show: (state: { label: string; tone?: PageBannerTone; duration?: number }) => void;
  dismiss: () => void;
}

const Ctx = createContext<PageBannerContextValue | null>(null);

/**
 * Provider pentru notificările de tip success/info la nivel de pagină.
 * Montat în `(dashboard)/layout.tsx`. Banner-ul vizual e randat de
 * `<PageBannerOverlay />` din TopBar (overlay absolut peste secțiunea companiei).
 *
 * Înlocuiește `toast.success(...)` / `toast.info(...)` în paginile de dashboard.
 * `toast.error(...)` rămâne pe Sonner — erorile au tratament distinct vizual.
 */
export function PageBannerProvider({ children }: { children: ReactNode }) {
  const [banner, setBanner] = useState<PageBannerState | null>(null);
  const counterRef = useRef(0);

  const show = useCallback<PageBannerContextValue['show']>((state) => {
    counterRef.current += 1;
    setBanner({
      id: counterRef.current,
      label: state.label,
      tone: state.tone ?? 'success',
      duration: state.duration ?? 3000,
    });
  }, []);

  const dismiss = useCallback(() => {
    setBanner(null);
  }, []);

  const value = useMemo<PageBannerContextValue>(
    () => ({ banner, show, dismiss }),
    [banner, show, dismiss],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Hook pentru a trigera banner-uri de la nivel de pagină.
 *
 * Usage:
 * ```tsx
 * const pageBanner = usePageBanner();
 * pageBanner.show({ label: 'Factură ștearsă', tone: 'success' });
 * ```
 *
 * În afara `<PageBannerProvider>` (ex. login page) returnează un fallback
 * silent — apelurile nu fac nimic, dar nu aruncă eroare.
 */
export function usePageBanner(): Pick<PageBannerContextValue, 'show' | 'dismiss'> {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Silent fallback — pentru pagini montate în afara dashboard layout (ex. login)
    return { show: () => {}, dismiss: () => {} };
  }
  return { show: ctx.show, dismiss: ctx.dismiss };
}

/** Internal — folosit de `<PageBannerOverlay />`. Nu folosi în pagini. */
export function usePageBannerState(): PageBannerState | null {
  const ctx = useContext(Ctx);
  return ctx?.banner ?? null;
}

/** Internal — folosit de `<PageBannerOverlay />` ca să ștergă state după slide-out. */
export function usePageBannerDismiss(): () => void {
  const ctx = useContext(Ctx);
  return ctx?.dismiss ?? (() => {});
}
